/**
 * Plan-quota helpers. The backend enforces hard limits (HTTP 402 on link
 * generation, skipped conversions over quota) and returns per-metric usage plus
 * `over_plan_quota_*` flags. These helpers derive a display level so Dashboard
 * and Billing can show a persistent "approaching / over your plan limit" banner.
 */

export interface UsageMetric {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
}

export interface UsageBreakdown {
  conversions?: UsageMetric;
  affiliates?: UsageMetric;
}

export type QuotaLevel = 'ok' | 'approaching' | 'over';

/** A limit of -1 means unlimited (Enterprise). */
export function quotaLevel(metric?: UsageMetric): QuotaLevel {
  if (!metric || metric.limit === -1) return 'ok';
  if (metric.limit > 0 && metric.used >= metric.limit) return 'over';
  if (metric.percentage >= 80) return 'approaching';
  return 'ok';
}

/** Worst level across all metrics: over > approaching > ok. */
export function overallQuotaLevel(usage?: UsageBreakdown): QuotaLevel {
  const levels = [quotaLevel(usage?.conversions), quotaLevel(usage?.affiliates)];
  if (levels.includes('over')) return 'over';
  if (levels.includes('approaching')) return 'approaching';
  return 'ok';
}

/** Any truthy `over_plan_quota_*` flag on a success payload means over quota. */
export function quotaLevelFromFlags(flags?: Record<string, unknown> | null): QuotaLevel {
  if (!flags) return 'ok';
  const over = Object.entries(flags).some(
    ([key, value]) => key.startsWith('over_plan_quota') && value === true
  );
  return over ? 'over' : 'ok';
}
